// client/src/components/pages/CustomSummaries/ShareSummaryModal.js
import React, { useState, useEffect } from 'react';
import { FaTimes, FaShare, FaUser, FaTrash, FaPlus } from 'react-icons/fa';
import api from '../../../services/api';
import LoadingSpinner from '../../common/LoadingSpinner';
import ThemedSelect from '../../common/ThemedSelect';

const ShareSummaryModal = ({ summary, onClose }) => {
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [newShare, setNewShare] = useState({
    sharedWith: '',
    shareType: 'view',
  });

  useEffect(() => {
    fetchShares();
  }, [summary.SummaryID]);

  const fetchShares = async () => {
    try {
      setLoading(true);
      const response = await api.customSummaries.shares.getAll(summary.SummaryID);
      setShares(response.data || []);
    } catch (err) {
      console.error('Error fetching shares:', err);
      setError('Failed to load sharing information');
    } finally {
      setLoading(false);
    }
  };

  const handleAddShare = async () => {
    if (!newShare.sharedWith.trim()) return;

    try {
      setSaving(true);
      await api.customSummaries.shares.create(summary.SummaryID, newShare);
      setNewShare({ sharedWith: '', shareType: 'view' });
      await fetchShares();
    } catch (err) {
      console.error('Error adding share:', err);
      setError(err.response?.data?.message || 'Failed to add share');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveShare = async (shareId) => {
    try {
      await api.customSummaries.shares.delete(summary.SummaryID, shareId);
      await fetchShares();
    } catch (err) {
      console.error('Error removing share:', err);
      setError(err.response?.data?.message || 'Failed to remove share');
    }
  };

  const shareTypeLabels = {
    view: 'View Only',
    execute: 'View & Execute',
    edit: 'View, Execute & Edit',
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-surface rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <FaShare className="text-primary-600" />
            <div>
              <h2 className="text-xl font-bold text-gray-900">Share Summary</h2>
              <p className="text-gray-600">{summary.Title}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
          >
            <FaTimes size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 max-h-[calc(90vh-140px)] overflow-y-auto">
          {/* Add New Share */}
          <div className="mb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Add New Share</h3>
            <div className="flex space-x-3">
              <input
                type="email"
                value={newShare.sharedWith}
                onChange={(e) => setNewShare((prev) => ({ ...prev, sharedWith: e.target.value }))}
                placeholder="Enter email address"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
              <ThemedSelect
                value={newShare.shareType}
                onChange={(e) => setNewShare((prev) => ({ ...prev, shareType: e.target.value }))}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="view">View Only</option>
                <option value="execute">View & Execute</option>
                <option value="edit">View, Execute & Edit</option>
              </ThemedSelect>
              <button
                onClick={handleAddShare}
                disabled={saving || !newShare.sharedWith.trim()}
                className="flex items-center px-4 py-2 bg-primary-600 hover:bg-primary-700 disabled:bg-primary-400 text-white rounded-lg transition-colors duration-200"
              >
                {saving ? <LoadingSpinner size="sm" /> : <FaPlus />}
              </button>
            </div>
          </div>

          {/* Current Shares */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Current Shares</h3>

            {loading ? (
              <div className="text-center py-8">
                <LoadingSpinner size="md" />
                <p className="text-gray-600 mt-2">Loading shares...</p>
              </div>
            ) : shares.length === 0 ? (
              <div className="text-center py-8">
                <FaUser className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <p className="text-gray-600">This summary is not shared with anyone yet.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {shares.map((share) => (
                  <div
                    key={share.ShareID}
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                  >
                    <div className="flex items-center space-x-3">
                      <FaUser className="text-gray-400" />
                      <div>
                        <div className="font-medium text-gray-900">{share.SharedWith}</div>
                        <div className="text-sm text-gray-500">
                          {shareTypeLabels[share.ShareType]} • Shared{' '}
                          {new Date(share.SharedAt).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => handleRemoveShare(share.ShareID)}
                      className="text-red-500 hover:text-red-700 transition-colors duration-200"
                      title="Remove share"
                    >
                      <FaTrash />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Public Access Info */}
          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h4 className="font-medium text-blue-900 mb-2">Public Access</h4>
            <p className="text-sm text-blue-700">
              {summary.IsPublic
                ? 'This summary is public and can be viewed by all users.'
                : 'This summary is private. Only you and users listed above can access it.'}
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700">{error}</p>
              <button
                onClick={() => setError(null)}
                className="mt-2 text-red-600 hover:text-red-800 underline text-sm"
              >
                Dismiss
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors duration-200"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ShareSummaryModal;
